import React, { useEffect, useMemo, useState } from "react";
import { Location, Map1 } from "iconsax-reactjs";
import { ACHIcon, RTPIcon, WireIcon } from "asset/icons";

import {
    resetCrossBorderTransfer,
    setCrossBorderUsdAll,
    setCrossBorderProvider,
} from "redux/slices/crossBorderTransferSlice";

import { useNavigate } from "react-router-dom";
import OnboardingLayout from "component/auth/OnboardingLayout";
import PaymentStepper from "component/pay/PaymentStepper";
import {
    ArrowDown2,
    ArrowRight2,
    PercentageCircle,
    ReceiptDiscount,
    Timer1,
} from "iconsax-reactjs";
import { useSelector, useDispatch } from "react-redux";
import CurrencyToggle from "component/currencyToggle";
import { CurrencySelector } from "component/wallet/CurrencySelector";
import { formatMoney } from "utilities/formatMoney";
import { fetchFxRate } from "api/fetchers";
import {
    setCrossBorderFromCurrency,
    setCrossBorderToCurrency,
    setCrossBorderAmount,
    setCrossBorderRateStart,
    setCrossBorderRateSuccess,
    setCrossBorderRateError,
} from "redux/slices/crossBorderTransferSlice";
import { fetchTransferFees } from "api/fetchers";
import { InputNumberFormat, format } from "@react-input/number-format";
import { resetBankTransfer } from "../../../../redux/slices/bankTransferSlice";
import { resetUsdTransfer } from "../../../../redux/slices/usdTransferSlice";
import { resetTransfer } from "../../../../redux/slices/tradevuTransferSlice";
import { resetCryptoTransfer } from "../../../../redux/slices/cryptoTransferSlice";
import { resetCryptoFiatTransfer } from "../../../../redux/slices/cryptoFiatTransferSlice";
import RecipientTypeModal from "component/pay/RecipientTypeModal";
import SelectBankProviderModal from "component/wallet/SelectBankProviderModal";
import useDebouncedValue from "hooks/useDebouncedValue";

const DEBOUNCE_MS = 1000;
const INPUT_DEBOUNCE_MS = 600;
const AMOUNT_LOCALE = "en";

const CrossBorderOtherUsers = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { business, currency: currentCurrency } = useSelector((s) => s);
    const wallets = useSelector((s) => s.wallets || []);
    const isBusinessApproved = business?.status === "APPROVED";
    const businessId = business?._id;

    const [amount, setAmountLocal] = useState("");
    const [toCurrency, setToCurrencyLocal] = useState("");

    const [rate, setRate] = useState(null);
    const [rateQuote, setRateQuote] = useState({ key: "", fingerprint: "" });
    const [rateLoading, setRateLoading] = useState(false);
    const [rateError, setRateError] = useState(null);
    const [feeLoading, setFeeLoading] = useState(false);
    const [feeReady, setFeeReady] = useState(false);
    const [feePercent, setFeePercent] = useState(0);
    const [feeFlat, setFeeFlat] = useState(0);
    const [txnFee, setTxnFee] = useState(0);

    const [feeIsPercent, setFeeIsPercent] = useState(false);
    const [maxFromAmount, setMaxFromAmount] = useState(0);
    const [minFromAmount, setMinFromAmount] = useState(0);

    // limits/usage
    const [dailyLimit, setDailyLimit] = useState(0);
    const [monthlyLimit, setMonthlyLimit] = useState(0);
    const [currentDaily, setCurrentDaily] = useState(0);
    const [currentMonthly, setCurrentMonthly] = useState(0);

    const isToUSD = String(toCurrency || "").toUpperCase() === "USD";

    // reset other flows
    useEffect(() => {
        dispatch(resetBankTransfer());
        dispatch(resetUsdTransfer());
        dispatch(resetTransfer());
        dispatch(resetCryptoTransfer());
        dispatch(resetCryptoFiatTransfer());
        dispatch(resetCrossBorderTransfer());
    }, [dispatch]);

    const [showRecipientLocationModal, setShowRecipientLocationModal] = useState(false);
    const [showTransferMethodModal, setShowTransferMethodModal] = useState(false);
    const [recipientLocation, setRecipientLocation] = useState("");

    const [paymentRail, setPaymentRail] = useState("");
    const [provider, setProviderState] = useState(null);
    const [showProviderModal, setShowProviderModal] = useState(false);

    const numericAmount = Number.parseFloat(amount || "0") || 0;
    const debouncedNumericAmount = useDebouncedValue(numericAmount, INPUT_DEBOUNCE_MS);
    const isAmountSettling = numericAmount !== debouncedNumericAmount;
    const usdPricingInputsReady = Boolean(
        isToUSD && debouncedNumericAmount > 0 && recipientLocation && paymentRail
    );

    const usdSelectionPrompt = !recipientLocation
        ? "Select recipient location"
        : recipientLocation === "US" && !paymentRail
          ? "Select payment method"
          : "";

    const currentRateFingerprint = [
        String(currentCurrency || "").toUpperCase(),
        String(toCurrency || "").toUpperCase(),
        String(numericAmount),
        isToUSD ? paymentRail : "",
    ].join("|");
    const debouncedRateFingerprint = [
        String(currentCurrency || "").toUpperCase(),
        String(toCurrency || "").toUpperCase(),
        String(debouncedNumericAmount),
        isToUSD ? paymentRail : "",
    ].join("|");
    const rateKey =
        rateQuote.fingerprint === currentRateFingerprint ? String(rateQuote.key || "") : "";

    useEffect(() => {
        if (!isToUSD || !recipientLocation) return;
        const accountType = recipientLocation === "US" ? "us" : "iban";
        sessionStorage.setItem("usdRecipientLocation", recipientLocation);
        sessionStorage.setItem("recipientType", recipientLocation);
        dispatch(setCrossBorderUsdAll({ account_type: accountType }));
    }, [isToUSD, recipientLocation, dispatch]);

    useEffect(() => {
        if (!isToUSD || !paymentRail) return;
        sessionStorage.setItem("paymentRail", paymentRail);
        sessionStorage.setItem("usdPaymentMethod", paymentRail);
        dispatch(setCrossBorderUsdAll({ paymentRail }));
    }, [isToUSD, paymentRail, dispatch]);

    const transferMethodLabel = {
        ach: "ACH",
        rtp: "RTP",
        wire: "Fedwire Transfer",
    }[paymentRail] || "Select payment method";

    const recipientLocationLabel =
        recipientLocation === "US"
            ? "Recipient in the US"
            : recipientLocation === "NON_US"
              ? "Recipient outside the US"
              : "Select recipient location";

    const currentWallet = useMemo(
        () =>
            wallets.find(
                (w) => (w.currency || "").toUpperCase() === String(currentCurrency).toUpperCase()
            ),
        [wallets, currentCurrency]
    );
    const isKES = String(currentCurrency || "").toUpperCase() === "KES";
    const providers = useMemo(
        () => (Array.isArray(currentWallet?.providers) ? currentWallet.providers : []),
        [currentWallet]
    );
    const hasMultipleProviders = providers.length > 1 && !isKES;
    const needsProvider = hasMultipleProviders && !provider;

    useEffect(() => {
        setProviderState(null);
    }, [currentCurrency]);

    const selectedProviderBalance = useMemo(() => {
        if (!hasMultipleProviders || !provider) return null;
        const value = Number(provider?.balance);
        return Number.isFinite(value) ? value : null;
    }, [hasMultipleProviders, provider]);

    const balance =
        selectedProviderBalance !== null
            ? selectedProviderBalance
            : Number(currentWallet?.balance || 0);

    useEffect(() => {
        if (currentCurrency) {
            dispatch(setCrossBorderFromCurrency(String(currentCurrency).toUpperCase()));
        }
    }, [currentCurrency, dispatch]);

    useEffect(() => {
        if (
            !businessId ||
            !toCurrency ||
            debouncedNumericAmount <= 0 ||
            !rateKey ||
            (isToUSD && !usdPricingInputsReady)
        ) {
            setFeeLoading(false);
            setFeeReady(false);
            setTxnFee(0);
            return;
        }

        const controller = new AbortController();
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                setFeeLoading(true);
                if (isToUSD) {
                    setFeeReady(false);
                    setMinFromAmount(0);
                    setMaxFromAmount(0);
                }

                const res = await fetchTransferFees({
                    businessId,
                    params: {
                        currency: String(currentCurrency).toUpperCase(), // FROM
                        currencyTo: String(toCurrency).toUpperCase(), // TO
                        amount: debouncedNumericAmount,
                        rateKey,
                        ...(isToUSD ? { paymentRail } : {}),
                    },
                    signal: controller.signal,
                });

                if (!res) throw new Error("Failed to fetch transfer fee");

                const raw = (res?.data ?? res) || {};

                const txnFeeNum = Number(raw.transactionFee) || 0;
                const minAny = Number(raw.minimumTransferAmount) || 0;
                const maxAny = Number(raw.maximumTransferAmount) || 0;

                const dLimit = Number(raw.dailyTransactionLimit) || 0;
                const mLimit = Number(raw.monthlyTransactionLimit) || 0;
                const dUsed = Number(raw.currentDailyTransfer) || 0;
                const mUsed = Number(raw.currentMonthlyTransfer) || 0;

                if (!cancelled) {
                    setFeePercent(0);
                    setFeeFlat(0);
                    setFeeIsPercent(false);

                    setTxnFee(txnFeeNum);
                    setMinFromAmount(minAny);
                    setMaxFromAmount(maxAny);

                    setDailyLimit(dLimit);
                    setMonthlyLimit(mLimit);
                    setCurrentDaily(dUsed);
                    setCurrentMonthly(mUsed);
                    if (isToUSD) setFeeReady(true);
                }
            } catch {
                if (!cancelled && isToUSD) setFeeReady(false);
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            controller.abort();
        };
    }, [
        businessId,
        currentCurrency,
        toCurrency,
        debouncedNumericAmount,
        paymentRail,
        recipientLocation,
        isToUSD,
        usdPricingInputsReady,
        rateKey,
    ]);

    useEffect(() => {
        if (toCurrency) return;
        const firstOther = wallets.find(
            (w) => (w.currency || "").toUpperCase() !== String(currentCurrency).toUpperCase()
        );
        if (firstOther?.currency) {
            const code = String(firstOther.currency).toUpperCase();
            setToCurrencyLocal(code);
            dispatch(setCrossBorderToCurrency(code));
        }
    }, [wallets, currentCurrency, toCurrency, dispatch]);

    const handleSelectToCurrency = (code) => {
        const upper = String(code).toUpperCase();
        setToCurrencyLocal(upper);
        dispatch(setCrossBorderToCurrency(upper));
    };

    const handleAmountChange = (e) => {
        const value = e.target.value || "";
        const cleaned = value
            .replace(/,/g, "")
            .replace(/[^\d.]/g, "")
            .replace(/^(\d*\.\d*).*$/, "$1");

        setAmountLocal(cleaned);
        dispatch(setCrossBorderAmount(cleaned));
    };
    const formatted = amount
        ? format(amount, { locales: AMOUNT_LOCALE, maximumFractionDigits: 2 })
        : "";

    useEffect(() => {
        const from = (currentCurrency || "").toUpperCase();
        const to = (toCurrency || "").toUpperCase();
        if (
            !businessId ||
            !from ||
            !to ||
            debouncedNumericAmount <= 0 ||
            (isToUSD && !usdPricingInputsReady)
        ) {
            setRate(null);
            setRateError(null);
            setRateQuote({ key: "", fingerprint: "" });
            return;
        }

        let cancelled = false;
        (async () => {
            setRateLoading(true);
            setRateError(null);
            dispatch(setCrossBorderRateStart());

            try {
                const resp = await fetchFxRate({
                    fromCurrency: from,
                    toCurrency: to,
                    amount: debouncedNumericAmount,
                    businessId,
                    ...(isToUSD ? { paymentRail } : {}),
                });
                const fx = resp;
                const roundedRate = Number(fx.roundedRate) || 0;
                const rateKeyStr = String(fx.rateKey ?? fx.key ?? "");
                const isOTC = Boolean(fx.isOTC);
                if (cancelled) return;

                setRate(roundedRate);
                setRateQuote({ key: rateKeyStr, fingerprint: debouncedRateFingerprint });
                dispatch(
                    setCrossBorderRateSuccess({
                        roundedRate,
                        rateKey: rateKeyStr,
                        isOTC,
                        fromCurrency: fx.fromCurrency || from,
                        toCurrency: fx.toCurrency || to,
                    })
                );
            } catch (e) {
                if (cancelled) return;
                const msg = e?.message || "Failed to fetch FX rate";
                setRate(null);
                setRateError(msg);
                setRateQuote({ key: "", fingerprint: "" });
                dispatch(setCrossBorderRateError(msg));
            } finally {
                if (!cancelled) setRateLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        currentCurrency,
        toCurrency,
        debouncedNumericAmount,
        paymentRail,
        isToUSD,
        usdPricingInputsReady,
        businessId,
        dispatch,
        debouncedRateFingerprint,
    ]);
    const isRateReady =
        !isAmountSettling &&
        !rateLoading &&
        typeof rate === "number" &&
        Number.isFinite(rate) &&
        rate > 0;

    const feeValue = useMemo(() => {
        const n = Number(txnFee);
        return Number.isFinite(n) && numericAmount > 0 ? Math.max(0, n) : 0;
    }, [txnFee, numericAmount]);

    const dailyRemaining = Math.max(0, dailyLimit - currentDaily);
    const monthlyRemaining = Math.max(0, monthlyLimit - currentMonthly);

    const afterDailyPct = dailyLimit > 0 ? (currentDaily + numericAmount) / dailyLimit : 0;
    const afterMonthlyPct = monthlyLimit > 0 ? (currentMonthly + numericAmount) / monthlyLimit : 0;

    const WARN_PCT = 0.9;

    const exceedDaily = dailyLimit > 0 && numericAmount > dailyRemaining;
    const exceedMonthly = monthlyLimit > 0 && numericAmount > monthlyRemaining;

    const nearDaily =
        dailyLimit > 0 && !exceedDaily && afterDailyPct >= WARN_PCT && numericAmount > 0;
    const nearMonthly =
        monthlyLimit > 0 && !exceedMonthly && afterMonthlyPct >= WARN_PCT && numericAmount > 0;

    const totalDebit = Math.max(0, numericAmount);
    const insufficientFunds = totalDebit + feeValue > balance;
    const pricingInputsReady =
        numericAmount > 0 && (!isToUSD || usdPricingInputsReady);
    const tooLow =
        pricingInputsReady && minFromAmount > 0 && numericAmount < minFromAmount;
    const tooHigh = maxFromAmount > 0 && numericAmount > maxFromAmount;

    const hasValidAmount =
        numericAmount > 0 &&
        !tooLow &&
        !tooHigh &&
        !insufficientFunds &&
        !exceedDaily &&
        !exceedMonthly;

    const convertedAmount = useMemo(() => {
        if (!isRateReady) return 0;
        const net = Math.max(0, numericAmount);
        return net * rate;
    }, [numericAmount, feeValue, rate, isRateReady]);
    const formattedConvertedAmount = useMemo(() => {
        const value = Number(convertedAmount);
        if (!Number.isFinite(value) || value === 0) return "0";
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    }, [convertedAmount]);

    const hasUsdOptions = !isToUSD || Boolean(recipientLocation && paymentRail);
    const canContinue =
        Boolean(toCurrency) &&
        isRateReady &&
        hasValidAmount &&
        hasUsdOptions &&
        (!isToUSD || (feeReady && !feeLoading)) &&
        !needsProvider;

    const handleContinue = () => {
        dispatch(setCrossBorderProvider(provider));
        navigate("/payment/send-money/cross-border/bank-recipient/recipient-info");
    };

    return (
        <OnboardingLayout title="Send to bank" showSkip={false}>
            <div className="max-w-2xl mx-auto mt-10 space-y-10 md:px-4 pb-32 md:pb-0">
                <PaymentStepper currentStep={0} />
                <h2 className="text-2xl font-normal">How much do you want to send?</h2>

                <div className="bg-white rounded-xl p-6 max-w-3xl w-full">
                    {/* Top row: label + CurrencyToggle */}
                    <div className="border-b pb-4 mb-4">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-[14px] font-[450] text-[#383B56]">You send</span>
                            <CurrencyToggle
                                className="w-full text-sm bg-transparent"
                                dropDownClassName="shadow-dropdown2 right-0 !top-[109%]"
                                fetchWallet
                                businessId={businessId}
                                isDisabled={!isBusinessApproved}
                                hideLabel
                            />
                        </div>

                        {/* Amount input */}
                        <div
                            className={`flex items-start text-3xl md:text-7xl font-normal leading-none ${
                                insufficientFunds ? "text-red-600" : ""
                            }`}
                        >
                            <InputNumberFormat
                                value={formatted}
                                onChange={handleAmountChange}
                                className={`!shadow-none outline-none font-thin w-full caret-[#6F00FF] ${
                                    insufficientFunds ? "border-b border-red-500" : ""
                                }`}
                                placeholder="0"
                                aria-invalid={insufficientFunds}
                                aria-describedby={insufficientFunds ? "amount-error" : undefined}
                                min="0"
                                type="currency"
                                locales={AMOUNT_LOCALE}
                            />
                        </div>

                        <div
                            className={`flex ${
                                insufficientFunds
                                    ? "justify-between"
                                    : "justify-between items-start"
                            }`}
                        >
                            <div className="text-sm space-y-1">
                                {exceedDaily && (
                                    <div role="alert" className="text-red-600">
                                        This amount exceeds your <b>daily limit</b>. Remaining
                                        today: {dailyRemaining.toLocaleString()}{" "}
                                        {String(currentCurrency).toUpperCase()}.
                                    </div>
                                )}
                                {exceedMonthly && (
                                    <div role="alert" className="text-red-600">
                                        This amount exceeds your <b>monthly limit</b>. Remaining
                                        this month: {monthlyRemaining.toLocaleString()}{" "}
                                        {String(currentCurrency).toUpperCase()}.
                                    </div>
                                )}

                                {insufficientFunds ? (
                                    <div id="amount-error" role="alert" className="text-red-600">
                                        Insufficient balance. Available: {balance.toLocaleString()}{" "}
                                        {String(currentCurrency).toUpperCase()}
                                    </div>
                                ) : tooLow ? (
                                    <div id="amount-error" role="alert" className="text-red-600">
                                        Minimum transfer amount is {minFromAmount.toLocaleString()}{" "}
                                        {String(currentCurrency).toUpperCase()}
                                    </div>
                                ) : tooHigh ? (
                                    <div id="amount-error" role="alert" className="text-red-600">
                                        Maximum transfer amount is {maxFromAmount.toLocaleString()}{" "}
                                        {String(currentCurrency).toUpperCase()}
                                    </div>
                                ) : (
                                    <>
                                        {nearDaily && (
                                            <div className="text-amber-600">
                                                Heads-up: this will put you close to your{" "}
                                                <b>daily</b> limit. Remaining today:{" "}
                                                {dailyRemaining.toLocaleString()}{" "}
                                                {String(currentCurrency).toUpperCase()}.
                                            </div>
                                        )}
                                        {nearMonthly && (
                                            <div className="text-amber-600">
                                                Heads-up: this will put you close to your{" "}
                                                <b>monthly</b> limit. Remaining this month:{" "}
                                                {monthlyRemaining.toLocaleString()}{" "}
                                                {String(currentCurrency).toUpperCase()}.
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="text-right">
                                <div className="text-[#6B7280] text-sm">
                                    Balance: {balance.toLocaleString()}{" "}
                                    {String(currentCurrency).toUpperCase()}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Fee + Rate block */}
                    <div className="relative my-6">
                        <div className="absolute top-0 left-0 right-0 h-px bg-[#E1E3F4]" />
                        <div className="absolute bottom-0 left-0 right-0 h-px bg-[#E1E3F4]" />
                        <div className="absolute left-4 top-[0px] bottom-[0px] w-px bg-[#E1E3F4] z-0" />
                        <span className="absolute left-[9px] top-[111px]">
                            <ArrowDown2 size={16} color="#E1E3F4" />
                        </span>

                        <div className="py-4 space-y-4">
                            {isToUSD && (
                                <div className="flex items-start gap-3 relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <ReceiptDiscount size={16} color="#6F739B" variant="Bulk" />
                                    </div>
                                    <div className="flex mt-1.5 justify-between items-center w-full">
                                        <span className="text-[#161827] text-[16px] font-[450]">
                                            Recipient location
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setShowRecipientLocationModal(true)}
                                            className="text-[#101828] inline-flex items-center gap-1"
                                            aria-haspopup="dialog"
                                        >
                                            <span className="text-[#6F00FF] font-[450] text-[16px]">
                                                {recipientLocationLabel}
                                            </span>
                                            <ArrowRight2 size={20} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {isToUSD && recipientLocation === "US" && (
                                <div className="flex items-start gap-3 relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <ReceiptDiscount size={16} color="#6F739B" variant="Bulk" />
                                    </div>
                                    <div className="flex mt-1.5 justify-between items-center w-full">
                                        <span className="text-[#161827] text-[16px] font-[450]">
                                            Payment method
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setShowTransferMethodModal(true)}
                                            className="text-[#101828] inline-flex items-center gap-1"
                                            aria-haspopup="dialog"
                                        >
                                            <span className="text-[#6F00FF] font-[450] text-[16px]">
                                                {transferMethodLabel}
                                            </span>
                                            <ArrowRight2 size={20} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {hasMultipleProviders && (
                                <div className="flex items-start gap-3 relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <ReceiptDiscount size={16} color="#6F739B" variant="Bulk" />
                                    </div>
                                    <div className="flex mt-1.5 justify-between items-center w-full">
                                        <span className="text-[#161827] text-[16px] font-[450]">
                                            {provider ? (
                                                <>
                                                    Sending via{" "}
                                                    {provider.bankName || provider.bank || "—"} (
                                                    {provider.accountNumber || "—"})
                                                </>
                                            ) : (
                                                "No bank account selected"
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setShowProviderModal(true)}
                                            className="text-[#101828] inline-flex items-center gap-1"
                                            aria-haspopup="dialog"
                                        >
                                            <span className="text-[#6F00FF] font-[450] text-[16px]">
                                                {provider ? "Change bank" : "Select bank"}
                                            </span>
                                            <ArrowRight2 size={20} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                    <PercentageCircle size={16} color="#6F739B" variant="Bulk" />
                                </div>
                                <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                    <span>Rate</span>
                                    <span>
                                        {!toCurrency ? (
                                            "Select a currency"
                                        ) : !pricingInputsReady ? (
                                            numericAmount <= 0
                                                ? "Enter an amount"
                                                : usdSelectionPrompt
                                        ) : rateLoading ? (
                                            "Fetching rate…"
                                        ) : rateError ? (
                                            <span className="text-red-600" role="alert">
                                                {rateError}
                                            </span>
                                        ) : (
                                            `1 ${String(
                                                currentCurrency
                                            ).toUpperCase()} = ${rate} ${String(
                                                toCurrency
                                            ).toUpperCase()}`
                                        )}
                                    </span>
                                </div>
                            </div>

                            {!isToUSD && (
                                <div className="flex items-start gap-3 relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <ReceiptDiscount
                                            size={16}
                                            color="#6F739B"
                                            variant="Bulk"
                                        />
                                    </div>
                                    <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                        <span>Fee</span>
                                        <span>
                                            {!toCurrency
                                                ? "Select a currency"
                                                : feeLoading
                                                  ? "Fetching fee…"
                                                  : `${formatMoney(feeValue)} ${String(
                                                        currentCurrency
                                                    ).toUpperCase()}`}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {isToUSD && (
                                <div className="flex items-start gap-3 relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <ReceiptDiscount
                                            size={16}
                                            color="#6F739B"
                                            variant="Bulk"
                                        />
                                    </div>
                                    <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                        <span>Transfer Fee</span>
                                        <span>
                                            {!pricingInputsReady
                                                ? numericAmount <= 0
                                                    ? "Enter an amount"
                                                    : usdSelectionPrompt
                                                : !rateKey
                                                  ? "Waiting for rate…"
                                                : feeLoading
                                                  ? "Fetching fee…"
                                                  : `${formatMoney(feeValue)} ${String(
                                                        currentCurrency
                                                    ).toUpperCase()}`}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recipient gets + CurrencySelector */}
                    <div className="pt-4 border-t">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-500">Recipient gets</span>
                            <CurrencySelector
                                wallets={wallets.filter(
                                    (w) =>
                                        (w.currency || "").toUpperCase() !==
                                        String(currentCurrency).toUpperCase()
                                )}
                                selected={toCurrency}
                                onSelect={handleSelectToCurrency}
                                exclude={[String(currentCurrency).toUpperCase()]}
                                currencyFrom={String(currentCurrency || "").toUpperCase()}
                            />
                        </div>

                        <div className="flex items-start text-3xl md:text-7xl font-thin leading-none">
                            <span>{formattedConvertedAmount}</span>
                        </div>
                    </div>
                </div>

                {/* Desktop/tablet footer */}
                <div className="max-w-2xl mx-auto mb-5 hidden md:block">
                    <div className="flex justify-between items-center border-t border-[#E5E7EB]">
                        <div className="flex items-center gap-2 text-[#6B7280] text-sm">
                            <Timer1 size="22" color="#6F739B" />
                            <span>Arrives in minutes</span>
                        </div>

                        <div className="flex items-center gap-4">
                            <button
                                className="text-[#111827] text-[16px] font-[450]"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                disabled={!canContinue}
                                className={`rounded-xl px-6 py-2 text-white font-semibold text-[16px] ${
                                    !canContinue
                                        ? "bg-[#E5DFFF] cursor-not-allowed"
                                        : "bg-[#7A27FF] hover:bg-[#6A1BFF]"
                                }`}
                                onClick={handleContinue}
                                title={
                                    needsProvider
                                        ? "Select a bank account"
                                        : !toCurrency
                                        ? "Select a currency"
                                        : rateLoading
                                          ? "Waiting for rate…"
                                          : tooLow
                                            ? `Minimum is ${minFromAmount.toLocaleString()} ${String(
                                                  currentCurrency
                                              ).toUpperCase()}`
                                            : exceedDaily
                                              ? `Exceeds daily limit. Remaining: ${dailyRemaining.toLocaleString()} ${String(
                                                    currentCurrency
                                                ).toUpperCase()}`
                                              : exceedMonthly
                                                ? `Exceeds monthly limit. Remaining: ${monthlyRemaining.toLocaleString()} ${String(
                                                      currentCurrency
                                                  ).toUpperCase()}`
                                                : !hasValidAmount
                                                  ? "Enter a valid amount"
                                                  : ""
                                }
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile sticky footer */}
                <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[#F6F7FD] border-t border-[#E7E9FB]">
                    <div
                        className="max-w-2xl mx-auto px-4 pt-2.5 pb-3"
                        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 6px)" }}
                    >
                        <div className="flex items-center justify-center mb-2 text-[#161827]">
                            <Timer1 size="22" color="#6F739B" />
                            <span className="ml-2 text-[15px] font-[450]">Arrives in minutes</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                className="text-[#111827] text-[16px] font-[450] w-[48%]"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                disabled={!canContinue}
                                onClick={handleContinue}
                                className={`h-12 rounded-xl px-5 font-semibold text-white text-[16px] w-[48%] ${
                                    !canContinue
                                        ? "bg-[#E5DFFF] cursor-not-allowed"
                                        : "bg-[#7A27FF]"
                                }`}
                                title={
                                    !toCurrency
                                        ? "Select a currency"
                                        : rateLoading
                                          ? "Waiting for rate…"
                                          : tooLow
                                            ? `Minimum is ${minFromAmount.toLocaleString()} ${String(
                                                  currentCurrency
                                              ).toUpperCase()}`
                                            : exceedDaily
                                              ? `Exceeds daily limit. Remaining: ${dailyRemaining.toLocaleString()} ${String(
                                                    currentCurrency
                                                ).toUpperCase()}`
                                              : exceedMonthly
                                                ? `Exceeds monthly limit. Remaining: ${monthlyRemaining.toLocaleString()} ${String(
                                                      currentCurrency
                                                  ).toUpperCase()}`
                                                : !hasValidAmount
                                                  ? "Enter a valid amount"
                                                  : ""
                                }
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <RecipientTypeModal
                open={showRecipientLocationModal}
                active={recipientLocation}
                title="Recipient bank location"
                options={[
                    {
                        id: "US",
                        title: "Recipient bank in the US",
                        desc: "Send using routing and account numbers to US-based banks",
                        icon: <Location />,
                    },
                    {
                        id: "NON_US",
                        title: "Recipient bank outside the US",
                        desc: "Send using IBAN to banks outside the US",
                        icon: <Map1 />,
                    },
                ]}
                onSelect={(id) => {
                    setRecipientLocation(id);
                    setPaymentRail((currentRail) => {
                        if (id === "NON_US") return "swift";
                        return recipientLocation === "NON_US" ? "" : currentRail;
                    });
                    setShowTransferMethodModal(false);
                    setShowRecipientLocationModal(false);
                }}
                onClose={() => setShowRecipientLocationModal(false)}
            />

            {recipientLocation === "US" && (
                <RecipientTypeModal
                    open={showTransferMethodModal}
                    active={paymentRail}
                    title="Payment method"
                    options={[
                        {
                            id: "ach",
                            title: "ACH",
                            desc: "Best for everyday bank transfers. Lower cost.",
                            icon: <ACHIcon />,
                        },
                        {
                            id: "rtp",
                            title: "RTP",
                            desc: "Best for faster transfers when supported.",
                            icon: <RTPIcon />,
                        },
                        {
                            id: "wire",
                            title: "Fedwire Transfer",
                            desc: "Best for large or urgent payments.",
                            icon: <WireIcon />,
                        },
                    ]}
                    onSelect={(id) => {
                        setPaymentRail(id);
                        setShowTransferMethodModal(false);
                    }}
                    onClose={() => setShowTransferMethodModal(false)}
                />
            )}

            <SelectBankProviderModal
                isOpen={showProviderModal}
                onClose={() => setShowProviderModal(false)}
                onSelect={(p) => setProviderState(p)}
                selectedId={provider?._id || provider?.accountNumber}
                providers={providers}
                currency={currentCurrency}
            />
        </OnboardingLayout>
    );
};

export default CrossBorderOtherUsers;
